import { Fragment, useEffect, useMemo, useState } from 'react';
import { useStaffing, useStages, type StageMapping } from '../../hooks/usePipeline';
import type { StaffingDeal, StaffingPersonRow } from '../../types';
import type { ActivityMode } from '../shared/ActivityIndicator';
import { DropdownToggle } from '../shared/DropdownToggle';
import { PersonHistoryModal } from './PersonHistoryModal';
import { StaffingDealLink, StaffingSourceProvider } from './StaffingDealLink';
import { StaffingHierarchyFilter } from './StaffingHierarchyFilter';
import {
  applyStaffingFilters,
  buildStageFilterGroups,
  buildTeamFilterGroups,
  emptyStaffingFilters,
  normalizeStaffingFilters,
  parseStaffingFiltersFromQuery,
  staffingFiltersActive,
  staffingFiltersEqual,
  staffingFiltersToSearch,
  type StaffingFilters,
} from './StaffingPage.filters';
import { formatSnapshotTimestamp } from '../../utils/formatTimestamp';
import styles from './StaffingPage.module.css';
import stylesV2 from './StaffingPageV2.module.css';

const ACTIVITY_COLORS = ['#A5D6A7', '#66BB6A', '#43A047', '#2E7D32', '#1B5E20'];
const EMPTY_STAGE_MAPPINGS: StageMapping[] = [];

function getActivityColor(value: number, max: number): string {
  if (value === 0 || max === 0) return '';
  const ratio = Math.min(value / max, 1);
  const idx = Math.min(Math.floor(ratio * ACTIVITY_COLORS.length), ACTIVITY_COLORS.length - 1);
  return ACTIVITY_COLORS[idx];
}

type ViewMode = 'people-deals' | 'deals-people';

// Extract the value for the current activity mode from a {meetings, emails} record.
function activityValue(
  counts: { meetings: number; emails: number } | undefined,
  mode: ActivityMode,
): number {
  if (!counts) return 0;
  if (mode === 'meetings') return counts.meetings;
  if (mode === 'emails') return counts.emails;
  return counts.meetings + counts.emails;
}

// Text shown in a cell. Total → "M / E"; single-metric → just that number.
function activityText(
  counts: { meetings: number; emails: number } | undefined,
  mode: ActivityMode,
): string {
  if (!counts) return '';
  if (mode === 'meetings') return counts.meetings > 0 ? String(counts.meetings) : '';
  if (mode === 'emails') return counts.emails > 0 ? String(counts.emails) : '';
  const total = counts.meetings + counts.emails;
  if (total === 0) return '';
  return `${counts.meetings}/${counts.emails}`;
}

export function StaffingPage() {
  const [activityDays, setActivityDays] = useState<7 | 30>(30);
  const { data, isLoading, error } = useStaffing(activityDays);
  const stagesQuery = useStages();
  const stageMappings = stagesQuery.data ?? EMPTY_STAGE_MAPPINGS;
  const [showActivity, setShowActivity] = useState(true);
  const [activityMode, setActivityMode] = useState<ActivityMode>('total');
  const [viewMode, setViewMode] = useState<ViewMode>('people-deals');
  const [filters, setFilters] = useState<StaffingFilters>(() =>
    typeof window === 'undefined'
      ? emptyStaffingFilters()
      : parseStaffingFiltersFromQuery(window.location.search),
  );

  // Hover state for cross-highlighting: when a deal is hovered, highlight
  // the names of people assigned to it; when a person is hovered, highlight
  // the deals they're assigned to. One is active at a time.
  const [hoverDealId, setHoverDealId] = useState<number | null>(null);
  const [hoverPersonName, setHoverPersonName] = useState<string | null>(null);

  // History modal — click a team-member name to open it
  const [historyEmail, setHistoryEmail] = useState<string | null>(null);
  const openHistory = (email: string) => setHistoryEmail(email);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = () => {
      setFilters(parseStaffingFiltersFromQuery(window.location.search));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const teamFilterGroups = useMemo(
    () => (data ? buildTeamFilterGroups(data) : []),
    [data],
  );
  const stageFilterGroups = useMemo(
    () => (data ? buildStageFilterGroups(data, stageMappings) : []),
    [data, stageMappings],
  );
  const normalizedFilters = useMemo(
    () => data
      ? normalizeStaffingFilters(filters, teamFilterGroups, stageFilterGroups)
      : filters,
    [data, filters, teamFilterGroups, stageFilterGroups],
  );

  useEffect(() => {
    if (!data || !stagesQuery.data) return;
    if (!staffingFiltersEqual(filters, normalizedFilters)) {
      setFilters(normalizedFilters);
    }
  }, [data, stagesQuery.data, filters, normalizedFilters]);

  useEffect(() => {
    if (!data || !stagesQuery.data || typeof window === 'undefined') return;
    const nextSearch = staffingFiltersToSearch(
      window.location.search,
      normalizedFilters,
      teamFilterGroups,
      stageFilterGroups,
    );
    const next = `${window.location.pathname}${nextSearch}${window.location.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) {
      window.history.replaceState(null, '', next);
    }
  }, [data, stagesQuery.data, normalizedFilters, teamFilterGroups, stageFilterGroups]);

  const filteredData = useMemo(
    () => data
      ? applyStaffingFilters(data, normalizedFilters, teamFilterGroups, stageMappings)
      : null,
    [data, normalizedFilters, teamFilterGroups, stageMappings],
  );

  // Ordered list of emails matching display order of the staffing grid (groups
  // then within-group order from useStaffing). Used for arrow-key navigation
  // in the history modal so users can page through team members in order.
  const orderedEmails = useMemo(() => {
    if (!filteredData) return [];
    const orderedPeople: StaffingPersonRow[] = [];
    for (const groupKey of filteredData.groups) {
      for (const p of filteredData.people.filter((x) => x.group === groupKey)) {
        orderedPeople.push(p);
      }
    }
    return orderedPeople.map((p) => p.email).filter(Boolean);
  }, [filteredData]);

  const dealAssignedCounts = useMemo(() => {
    if (!filteredData) return {};
    const counts: Record<number, number> = {};
    for (const person of filteredData.people) {
      for (const dealIdStr of Object.keys(person.deal_assignments)) {
        const dealId = Number(dealIdStr);
        counts[dealId] = (counts[dealId] || 0) + 1;
      }
    }
    return counts;
  }, [filteredData]);

  const maxActivity = useMemo(() => {
    if (!filteredData) return 1;
    let max = 0;
    for (const person of filteredData.people) {
      for (const counts of Object.values(person.deal_activity || {})) {
        const v = activityValue(counts, activityMode);
        if (v > max) max = v;
      }
    }
    return Math.max(1, max);
  }, [filteredData, activityMode]);

  if (isLoading) {
    return <div className={styles.loading}>Loading staffing data...</div>;
  }
  if (error || !data) {
    return (
      <div className={styles.error}>
        {error ? `Error: ${error.message}` : 'No data'}
      </div>
    );
  }

  // Post-Affinity S4 — see the note on StaffingPageV2: `unavailable` is a failure to read, and must
  // never render as an idle team.
  if (data.source === 'unavailable') {
    return (
      <div className={styles.error} role="status">
        Staffing could not be read. This is an explicit failure to read the native CRM sources, not an
        empty team &mdash; nothing is being served from the Affinity era, which stopped being written on
        8 Aug 2026. Reload, and if it persists the deal list is not resolving for your account.
      </div>
    );
  }

  const { snapshot_timestamp, list_id } = data;
  const { groups, group_labels, people, portfolio_deals, new_deals } = filteredData || data;
  const filtersActive = staffingFiltersActive(normalizedFilters);
  const headerSummary = filtersActive
    ? `${people.length} of ${data.people.length} team members \u00B7 ${portfolio_deals.length} of ${data.portfolio_deals.length} portfolio \u00B7 ${new_deals.length} of ${data.new_deals.length} active deals`
    : `${portfolio_deals.length} portfolio \u00B7 ${new_deals.length} active deals`;
  const noFilterMatches = filtersActive && (people.length === 0 || portfolio_deals.length + new_deals.length === 0);
  const clearFilters = () => setFilters(emptyStaffingFilters());

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.headerBar}>
          <div className={styles.headerLeft}>
            <span className={styles.headerLabel}>Team Staffing</span>
            <span className={styles.headerSub}>
              {headerSummary}
            </span>
            {snapshot_timestamp && (
              <span className={styles.headerSub}>
                &middot; as of {formatSnapshotTimestamp(snapshot_timestamp)}
              </span>
            )}
          </div>
          <div className={styles.headerControls}>
            <StaffingHierarchyFilter
              label="Team"
              groups={teamFilterGroups}
              selected={normalizedFilters.team}
              onChange={(team) => setFilters((prev) => ({ ...prev, team }))}
            />
            <StaffingHierarchyFilter
              label="Stage"
              groups={stageFilterGroups}
              selected={normalizedFilters.stage}
              onChange={(stage) => setFilters((prev) => ({ ...prev, stage }))}
            />
            <DropdownToggle<ViewMode>
              ariaLabel="View"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'people-deals', label: 'Team \u00D7 Deals' },
                { value: 'deals-people', label: 'Deals \u00D7 Team' },
              ]}
            />
            <DropdownToggle<'assignments' | 'activity'>
              ariaLabel="Show"
              value={showActivity ? 'activity' : 'assignments'}
              onChange={(v) => setShowActivity(v === 'activity')}
              options={[
                { value: 'assignments', label: 'Assignments' },
                { value: 'activity', label: 'Activity' },
              ]}
            />
            {showActivity && (
              <DropdownToggle<ActivityMode>
                ariaLabel="Activity metric"
                value={activityMode}
                onChange={setActivityMode}
                options={[
                  { value: 'total', label: 'Total' },
                  { value: 'meetings', label: 'Meetings' },
                  { value: 'emails', label: 'Emails' },
                ]}
              />
            )}
            <DropdownToggle<7 | 30>
              ariaLabel="Activity window"
              value={activityDays}
              onChange={setActivityDays}
              options={[
                { value: 7, label: '7d' },
                { value: 30, label: '30d' },
              ]}
            />
          </div>
        </div>

        {/* Every deal name inside the grid links through `StaffingDealLink`, which needs to know which
            era it is pointing at — see StaffingDealLink.tsx. */}
        <StaffingSourceProvider listId={list_id}>
          {noFilterMatches ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>No staffing matches</div>
              <div className={styles.emptySub}>The selected team and stage filters do not overlap.</div>
              <button className={styles.emptyClearBtn} onClick={clearFilters} type="button">
                Clear filters
              </button>
            </div>
          ) : viewMode === 'people-deals' ? (
            <PeopleByDealsView
              groups={groups}
              groupLabels={group_labels}
              people={people}
              portfolioDeals={portfolio_deals}
              newDeals={new_deals}
              dealAssignedCounts={dealAssignedCounts}
              showActivity={showActivity}
              maxActivity={maxActivity}
              activityDays={activityDays}
              activityMode={activityMode}
              hoverDealId={hoverDealId}
              hoverPersonName={hoverPersonName}
              setHoverDealId={setHoverDealId}
              setHoverPersonName={setHoverPersonName}
              openHistory={openHistory}
            />
          ) : (
            <DealsByPeopleView
              groups={groups}
              groupLabels={group_labels}
              people={people}
              portfolioDeals={portfolio_deals}
              newDeals={new_deals}
              showActivity={showActivity}
              maxActivity={maxActivity}
              activityDays={activityDays}
              activityMode={activityMode}
              hoverDealId={hoverDealId}
              hoverPersonName={hoverPersonName}
              setHoverDealId={setHoverDealId}
              setHoverPersonName={setHoverPersonName}
              openHistory={openHistory}
            />
          )}
        </StaffingSourceProvider>
      </div>
      {historyEmail && (
        <PersonHistoryModal
          email={historyEmail}
          orderedEmails={orderedEmails}
          onNavigate={(e) => setHistoryEmail(e)}
          onClose={() => setHistoryEmail(null)}
        />
      )}
    </div>
  );
}

/* ── People × Deals view (original) ── */

function PeopleByDealsView({
  groups,
  groupLabels,
  people,
  portfolioDeals,
  newDeals,
  dealAssignedCounts,
  showActivity,
  maxActivity,
  activityDays,
  activityMode,
  hoverDealId,
  hoverPersonName,
  setHoverDealId,
  setHoverPersonName,
  openHistory,
}: {
  groups: string[];
  groupLabels: Record<string, string>;
  people: StaffingPersonRow[];
  portfolioDeals: StaffingDeal[];
  newDeals: StaffingDeal[];
  dealAssignedCounts: Record<number, number>;
  showActivity: boolean;
  maxActivity: number;
  activityDays: number;
  activityMode: ActivityMode;
  hoverDealId: number | null;
  hoverPersonName: string | null;
  setHoverDealId: (id: number | null) => void;
  setHoverPersonName: (name: string | null) => void;
  openHistory: (name: string) => void;
}) {
  const totalDealCols = portfolioDeals.length + newDeals.length;

  // Precompute dealId → Set<person_name> for fast "is this person assigned" checks
  const dealPersons = useMemo(() => {
    const m: Record<number, Set<string>> = {};
    for (const p of people) {
      for (const dealIdStr of Object.keys(p.deal_assignments)) {
        const did = Number(dealIdStr);
        if (!m[did]) m[did] = new Set();
        m[did].add(p.name);
      }
    }
    return m;
  }, [people]);

  // Deal name cell gets highlighted when the currently-hovered person is
  // assigned to it. Person name cell gets highlighted when the currently-
  // hovered deal has that person assigned.
  const hoveredPerson = hoverPersonName ? people.find((p) => p.name === hoverPersonName) : null;

  const isDealHighlighted = (dealId: number): boolean => {
    if (!hoveredPerson) return false;
    return !!hoveredPerson.deal_assignments[dealId];
  };

  const isPersonHighlighted = (personName: string): boolean => {
    if (hoverDealId === null) return false;
    return dealPersons[hoverDealId]?.has(personName) ?? false;
  };

  return (
    <div className={styles.scrollContainer}>
      <table className={styles.matrix}>
        <thead>
          <tr className={styles.groupHeaderRow}>
            <th className={styles.stickyCol} rowSpan={2}>Team Member</th>
            <th></th>
            {portfolioDeals.length > 0 && (
              <th colSpan={portfolioDeals.length}>Portfolio ({portfolioDeals.length})</th>
            )}
            {newDeals.length > 0 && (
              <th colSpan={newDeals.length} className={styles.sectionDivider}>
                New Deals ({newDeals.length})
              </th>
            )}
          </tr>
          <tr className={styles.dealCountRow}>
            <th className={styles.thSmall} style={{ textAlign: 'right', color: '#999', fontWeight: 600 }}>Count</th>
            {portfolioDeals.map((d) => {
              const hi = isDealHighlighted(d.deal_key);
              return (
                <th
                  key={d.deal_key}
                  className={`${styles.thDeal}${hi ? ` ${styles.highlighted}` : ''}`}
                  onMouseEnter={() => setHoverDealId(d.deal_key)}
                  onMouseLeave={() => setHoverDealId(null)}
                >
                  <StaffingDealLink deal={d} label={abbreviate(d.name)} className={styles.dealLink} />
                </th>
              );
            })}
            {newDeals.map((d, i) => {
              const hi = isDealHighlighted(d.deal_key);
              return (
                <th
                  key={d.deal_key}
                  className={`${styles.thDeal}${i === 0 ? ` ${styles.sectionDivider}` : ''}${hi ? ` ${styles.highlighted}` : ''}`}
                  onMouseEnter={() => setHoverDealId(d.deal_key)}
                  onMouseLeave={() => setHoverDealId(null)}
                >
                  <StaffingDealLink deal={d} label={abbreviate(d.name)} className={styles.dealLink} />
                </th>
              );
            })}
          </tr>
          <tr className={styles.dealCountRow}>
            <th className={styles.stickyCol} style={{ textAlign: 'right', fontSize: 9, color: '#999' }}>Assigned</th>
            <th className={styles.thSmall}></th>
            {portfolioDeals.map((d) => (
              <th key={d.deal_key} className={styles.thSmall}>
                {dealAssignedCounts[d.deal_key] || ''}
              </th>
            ))}
            {newDeals.map((d, i) => (
              <th
                key={d.deal_key}
                className={`${styles.thSmall}${i === 0 ? ` ${styles.sectionDivider}` : ''}`}
              >
                {dealAssignedCounts[d.deal_key] || ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((groupKey) => {
            const groupPeople = people.filter((p) => p.group === groupKey);
            if (groupPeople.length === 0) return null;
            const colCount = 2 + totalDealCols;
            return (
              <Fragment key={groupKey}>
                <tr className={styles.tierRow}>
                  <td colSpan={colCount} className={styles.tierCell}>
                    {groupLabels[groupKey]}
                  </td>
                </tr>
                {groupPeople.map((person) => (
                  <PersonRow
                    key={person.name}
                    person={person}
                    portfolioDeals={portfolioDeals}
                    newDeals={newDeals}
                    showActivity={showActivity}
                    maxActivity={maxActivity}
                    activityDays={activityDays}
                    activityMode={activityMode}
                    highlighted={isPersonHighlighted(person.name)}
                    onHoverEnter={() => setHoverPersonName(person.name)}
                    onHoverLeave={() => setHoverPersonName(null)}
                    onNameClick={() => openHistory(person.email)}
                  />
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {showActivity && <ActivityLegend variant="v1" />}
    </div>
  );
}

function PersonRow({
  person,
  portfolioDeals,
  newDeals,
  showActivity,
  maxActivity,
  activityDays,
  activityMode,
  highlighted,
  onHoverEnter,
  onHoverLeave,
  onNameClick,
}: {
  person: StaffingPersonRow;
  portfolioDeals: StaffingDeal[];
  newDeals: StaffingDeal[];
  showActivity: boolean;
  maxActivity: number;
  activityDays: number;
  activityMode: ActivityMode;
  highlighted: boolean;
  onHoverEnter: () => void;
  onHoverLeave: () => void;
  onNameClick: () => void;
}) {
  function renderCell(dealId: number, extraClass?: string) {
    const assigned = !!person.deal_assignments[dealId];
    const cellClass = `${styles.matrixCell}${extraClass ? ` ${extraClass}` : ''}`;
    const counts = person.deal_activity?.[dealId];

    if (!assigned) {
      // Unassigned with activity → muted grey so the signal is visible but
      // clearly distinct from assigned deals.
      if (showActivity && counts) {
        const text = activityText(counts, activityMode);
        if (text) {
          const tip = `Not on deal team — ${counts.meetings} meetings, ${counts.emails} emails (${activityDays}d)`;
          return (
            <td key={dealId} className={cellClass}>
              <span className={`${styles.activityDot} ${styles.activityUnassigned}`} title={tip}>
                {text}
              </span>
            </td>
          );
        }
      }
      return <td key={dealId} className={cellClass} />;
    }

    if (showActivity) {
      const value = activityValue(counts, activityMode);
      const text = activityText(counts, activityMode);
      const bg = value > 0 ? getActivityColor(value, maxActivity) : '';
      const tip = counts
        ? `${counts.meetings} meetings, ${counts.emails} emails (${activityDays}d)`
        : `Assigned — 0 (${activityDays}d)`;
      return (
        <td key={dealId} className={cellClass}>
          <span
            className={`${styles.activityDot} ${value === 0 ? styles.activityEmpty : ''}`}
            style={bg ? { background: bg } : undefined}
            title={tip}
          >
            {value === 0 ? '✓' : text}
          </span>
        </td>
      );
    }
    return (
      <td key={dealId} className={cellClass}>
        <span className={styles.assigned}>&#x2713;</span>
      </td>
    );
  }

  return (
    <tr className={styles.personRow}>
      <td
        className={`${styles.td} ${styles.stickyCol} ${styles.nameCell}${highlighted ? ` ${styles.highlighted}` : ''}`}
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
      >
        <button
          className={styles.personNameBtn}
          onClick={onNameClick}
          title={`View history for ${person.name}`}
        >
          <span className={styles.personName}>{person.short_name}</span>
        </button>
      </td>
      <td className={`${styles.td} ${styles.countCell}`}>{person.total_count || ''}</td>
      {portfolioDeals.map((d) => renderCell(d.deal_key))}
      {newDeals.map((d, i) =>
        renderCell(d.deal_key, i === 0 ? styles.sectionDivider : undefined),
      )}
    </tr>
  );
}

/* ── Deals × People view (flipped) ── */

function DealsByPeopleView({
  groups,
  groupLabels,
  people,
  portfolioDeals,
  newDeals,
  showActivity,
  maxActivity,
  activityDays,
  activityMode,
  hoverDealId,
  hoverPersonName,
  setHoverDealId,
  setHoverPersonName,
  openHistory,
}: {
  groups: string[];
  groupLabels: Record<string, string>;
  people: StaffingPersonRow[];
  portfolioDeals: StaffingDeal[];
  newDeals: StaffingDeal[];
  showActivity: boolean;
  maxActivity: number;
  activityDays: number;
  activityMode: ActivityMode;
  hoverDealId: number | null;
  hoverPersonName: string | null;
  setHoverDealId: (id: number | null) => void;
  setHoverPersonName: (name: string | null) => void;
  openHistory: (name: string) => void;
}) {
  const personColumns: { person: StaffingPersonRow; groupKey: string }[] = [];
  for (const groupKey of groups) {
    const groupPeople = people.filter((p) => p.group === groupKey);
    for (const person of groupPeople) {
      personColumns.push({ person, groupKey });
    }
  }

  // For highlighting: look up the hovered person to know which deals they own;
  // look up the hovered deal to know which people are on it.
  const hoveredPerson = hoverPersonName ? people.find((p) => p.name === hoverPersonName) : null;
  const dealPersons = useMemo(() => {
    const m: Record<number, Set<string>> = {};
    for (const p of people) {
      for (const dealIdStr of Object.keys(p.deal_assignments)) {
        const did = Number(dealIdStr);
        if (!m[did]) m[did] = new Set();
        m[did].add(p.name);
      }
    }
    return m;
  }, [people]);

  const isPersonHighlighted = (personName: string): boolean => {
    if (hoverDealId === null) return false;
    return dealPersons[hoverDealId]?.has(personName) ?? false;
  };
  const isDealHighlighted = (dealId: number): boolean => {
    if (!hoveredPerson) return false;
    return !!hoveredPerson.deal_assignments[dealId];
  };

  return (
    <div className={stylesV2.scrollContainer}>
      <table className={stylesV2.matrix}>
        <thead>
          <tr className={stylesV2.groupHeaderRow}>
            <th className={stylesV2.stickyCol} rowSpan={2}>Deal</th>
            {groups.map((groupKey) => {
              const count = personColumns.filter((c) => c.groupKey === groupKey).length;
              if (count === 0) return null;
              return (
                <th key={groupKey} colSpan={count} className={stylesV2.groupTh}>
                  {groupLabels[groupKey]}
                </th>
              );
            })}
          </tr>
          <tr className={stylesV2.personHeaderRow}>
            {personColumns.map(({ person }) => {
              const hi = isPersonHighlighted(person.name);
              return (
                <th
                  key={person.name}
                  className={`${stylesV2.personTh}${hi ? ` ${stylesV2.highlighted}` : ''}`}
                  onMouseEnter={() => setHoverPersonName(person.name)}
                  onMouseLeave={() => setHoverPersonName(null)}
                >
                  <button
                    className={stylesV2.personNameBtn}
                    onClick={() => openHistory(person.email)}
                    title={`View history for ${person.name}`}
                  >
                    <span className={stylesV2.personName}>{person.short_name}</span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {portfolioDeals.length > 0 && (
            <Fragment>
              <tr className={stylesV2.sectionRow}>
                <td colSpan={1 + personColumns.length} className={stylesV2.sectionCell}>
                  Portfolio ({portfolioDeals.length})
                </td>
              </tr>
              {portfolioDeals.map((deal) => (
                <DealRow
                  key={deal.deal_key}
                  deal={deal}
                  dealId={deal.deal_key}
                  personColumns={personColumns}
                  showActivity={showActivity}
                  maxActivity={maxActivity}
                  activityDays={activityDays}
                  activityMode={activityMode}
                  highlighted={isDealHighlighted(deal.deal_key)}
                  onHoverEnter={() => setHoverDealId(deal.deal_key)}
                  onHoverLeave={() => setHoverDealId(null)}
                />
              ))}
            </Fragment>
          )}
          {newDeals.length > 0 && (
            <Fragment>
              <tr className={stylesV2.sectionRow}>
                <td colSpan={1 + personColumns.length} className={stylesV2.sectionCell}>
                  New Deals ({newDeals.length})
                </td>
              </tr>
              {newDeals.map((deal) => (
                <DealRow
                  key={deal.deal_key}
                  deal={deal}
                  dealId={deal.deal_key}
                  personColumns={personColumns}
                  showActivity={showActivity}
                  maxActivity={maxActivity}
                  activityDays={activityDays}
                  activityMode={activityMode}
                  highlighted={isDealHighlighted(deal.deal_key)}
                  onHoverEnter={() => setHoverDealId(deal.deal_key)}
                  onHoverLeave={() => setHoverDealId(null)}
                />
              ))}
            </Fragment>
          )}
        </tbody>
      </table>
      {showActivity && <ActivityLegend variant="v2" />}
    </div>
  );
}

function DealRow({
  deal,
  dealId,
  personColumns,
  showActivity,
  maxActivity,
  activityDays,
  activityMode,
  highlighted,
  onHoverEnter,
  onHoverLeave,
}: {
  // The deal itself, for the link — which id it uses depends on the era (`StaffingDealLink`).
  deal: StaffingDeal;
  dealId: number;
  personColumns: { person: StaffingPersonRow; groupKey: string }[];
  showActivity: boolean;
  maxActivity: number;
  activityDays: number;
  activityMode: ActivityMode;
  highlighted: boolean;
  onHoverEnter: () => void;
  onHoverLeave: () => void;
}) {
  return (
    <tr className={stylesV2.dealRow}>
      <td
        className={`${stylesV2.stickyCol} ${stylesV2.dealNameCell}${highlighted ? ` ${stylesV2.highlighted}` : ''}`}
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
      >
        <StaffingDealLink deal={deal} className={stylesV2.dealLink} />
      </td>
      {personColumns.map(({ person }) => {
        const assigned = !!person.deal_assignments[dealId];
        const counts = person.deal_activity?.[dealId];

        if (!assigned) {
          if (showActivity && counts) {
            const text = activityText(counts, activityMode);
            if (text) {
              const tip = `Not on deal team — ${counts.meetings} meetings, ${counts.emails} emails (${activityDays}d)`;
              return (
                <td key={person.name} className={stylesV2.matrixCell}>
                  <span className={`${stylesV2.activityDot} ${stylesV2.activityUnassigned}`} title={tip}>
                    {text}
                  </span>
                </td>
              );
            }
          }
          return <td key={person.name} className={stylesV2.matrixCell} />;
        }
        if (showActivity) {
          const value = activityValue(counts, activityMode);
          const text = activityText(counts, activityMode);
          const bg = value > 0 ? getActivityColor(value, maxActivity) : '';
          const tip = counts
            ? `${counts.meetings} meetings, ${counts.emails} emails (${activityDays}d)`
            : `Assigned — 0 (${activityDays}d)`;
          return (
            <td key={person.name} className={stylesV2.matrixCell}>
              <span
                className={`${stylesV2.activityDot} ${value === 0 ? stylesV2.activityEmpty : ''}`}
                style={bg ? { background: bg } : undefined}
                title={tip}
              >
                {value === 0 ? '✓' : text}
              </span>
            </td>
          );
        }
        return (
          <td key={person.name} className={stylesV2.matrixCell}>
            <span className={stylesV2.assigned}>&#x2713;</span>
          </td>
        );
      })}
    </tr>
  );
}

function abbreviate(name: string): string {
  return name.length > 18 ? name.slice(0, 17) + '\u2026' : name;
}

// Legend below the activity grid \u2014 explains the green-fill heat cells
// vs. the green-outlined \u2713 "assigned, no activity" cells. Two variants
// share identical markup but read styles from the right module.
function ActivityLegend({ variant }: { variant: 'v1' | 'v2' }) {
  const s = variant === 'v1' ? styles : stylesV2;
  return (
    <div className={s.legend}>
      <span className={s.legendItem}>
        <span className={s.legendSwatchActivity} />
        Activity (emails + meetings)
      </span>
      <span className={s.legendItem}>
        <span className={s.legendSwatchEmpty}>&#x2713;</span>
        Assigned, no activity
      </span>
    </div>
  );
}
